// Transaction DAO — data access for transactions and transports.
// Owns all query complexity. Each method = one Supabase call.
// Enriches results with computed pricing (Pricing per side).

import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base.ts';
import { enrichTransaction } from '../../transforms/transaction.ts';
import type {
  TransactionRow,
  Transaction
} from '@shared/types/transaction.ts';

// ── Select fragments ────────────────────────────────────────────────────

const COMPANY_FIELDS = 'id, short_name, full_name, nip, street, zip_code, city, country_code, fakturownia_client_id, parent_company_id, payment_terms';

const TRANSACTION_WITH_RELATIONS_SELECT = `*,
  product:product_id(id, name, waste_code, annex_description, gtu_code, invoice_names_load, invoice_names_unload, name_pl, name_en),
  load_commercial_company:load_commercial_company_id(${COMPANY_FIELDS}),
  unload_commercial_company:unload_commercial_company_id(${COMPANY_FIELDS}),
  load_place_company:load_place_company_id(${COMPANY_FIELDS}),
  unload_place_company:unload_place_company_id(${COMPANY_FIELDS}),
  exporter_company:exporter_company_id(id, short_name),
  importer_company:importer_company_id(id, short_name),
  logistics_employee:logistics_employee_id(id, full_name),
  load_sales_employee:load_sales_employee_id(id, full_name),
  unload_sales_employee:unload_sales_employee_id(id, full_name)`;

// ── TransactionDao ──────────────────────────────────────────────────────

export class TransactionDao extends BaseDao<TransactionRow> {
  protected readonly dependentResources = [
    'transactions',
    'transactions_with_checks',
    'transaction_transports',
  ];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'transactions', invalidate);
  }

  // --- Transaction queries ---

  /** Fetch multiple transactions by IDs with all FK relations + computed pricing. */
  async getByIds(ids: number[]): Promise<Transaction[]> {
    if (ids.length === 0) return [];
    const res = await this.sb
      .from('transactions_with_checks')
      .select(TRANSACTION_WITH_RELATIONS_SELECT)
      .in('id', ids);
    if (res.error) throw res.error;
    return ((res.data ?? []) as TransactionRow[]).map(enrichTransaction);
  }

  /** Return the most recent distinct order_number values (for autocomplete). */
  async listRecentOrderNumbers(limit = 50): Promise<string[]> {
    const { data, error } = await this.sb
      .from('transactions')
      .select('order_number')
      .not('order_number', 'is', null)
      .order('order_number', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return [...new Set(((data ?? []) as { order_number: string }[]).map(r => r.order_number).filter(Boolean))];
  }

  /**
   * Assign a logistician ONLY when the transaction has none (issue #296).
   * Single conditional statement — `.is('logistics_employee_id', null)` makes the
   * "don't overwrite" rule atomic instead of a read-then-write race.
   * Returns true when THIS call performed the assignment.
   */
  async assignLogisticianIfUnassigned(id: number, employeeId: number): Promise<boolean> {
    const { data, error } = await this.sb
      .from('transactions')
      .update({ logistics_employee_id: employeeId })
      .eq('id', id)
      .is('logistics_employee_id', null)
      .select('id');
    if (error) throw error;
    return ((data ?? []) as unknown[]).length > 0;
  }

}
